import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";

const THEMES = [
  {
    id: "maldives", name: "Azure Maldives", style: "Dark luxury",
    bg: "#0B1F33", fg: "#F4EFE6", accent: "#C49A5A", muted: "rgba(244,239,230,0.55)",
    img: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=900&q=70",
    tagline: "Barefoot luxury, privately arranged", pkgs: ["Overwater Serenity", "Atoll Hopping Private"],
  },
  {
    id: "thailand", name: "Siam Adventures", style: "Bright adventure",
    bg: "#FFF4E2", fg: "#2A1E12", accent: "#D96C2C", muted: "rgba(42,30,18,0.55)",
    img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=900&q=70",
    tagline: "Thailand, off the beaten path", pkgs: ["Jungle to Island", "Bangkok After Dark"],
  },
  {
    id: "vietnam", name: "Annam Journeys", style: "Minimal editorial",
    bg: "#F7F5F0", fg: "#232323", accent: "#8A9B7C", muted: "rgba(35,35,35,0.5)",
    img: "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=900&q=70",
    tagline: "Vietnam, slowly and quietly", pkgs: ["Lantern Towns", "Ha Long in Mist"],
  },
];

export default function WhiteLabel() {
  const [t, setT] = useState(THEMES[0]);

  return (
    <section id="white-label" className="py-24 lg:py-32">
      <div className="container-x">
        <Reveal><Chapter index={4} label="White-Label Demonstration" /></Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <h2 className="max-w-xl font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Your Destination. Your Brand. Your Digital Business.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base leading-relaxed text-stone2">
              Your customers interact with your brand. Travel-Tek stays behind the scenes.
              Switch the brand below — the platform underneath never changes.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="whitelabel-switcher">
            {THEMES.map((th) => (
              <button
                key={th.id}
                onClick={() => setT(th)}
                data-testid={`whitelabel-brand-toggle-${th.id}`}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                  t.id === th.id ? "border-ink bg-ink text-paper" : "border-sand bg-white text-stone2 hover:text-ink"
                }`}
              >
                {th.name} · <span className="font-normal opacity-70">{th.style}</span>
              </button>
            ))}
          </div>

          <div className="relative mt-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 18, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -12, scale: 0.99 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden rounded-2xl border border-sand shadow-[0_40px_80px_-40px_rgba(11,31,51,0.4)]"
                style={{ backgroundColor: t.bg }}
                data-testid="whitelabel-preview"
              >
                <div className="flex items-center gap-2 border-b px-4 py-2.5" style={{ borderColor: "rgba(128,128,128,0.2)" }}>
                  <span className="flex gap-1">
                    <i className="h-2 w-2 rounded-full" style={{ backgroundColor: t.accent }} />
                    <i className="h-2 w-2 rounded-full opacity-30" style={{ backgroundColor: t.fg }} />
                    <i className="h-2 w-2 rounded-full opacity-30" style={{ backgroundColor: t.fg }} />
                  </span>
                  <span className="mx-auto rounded-full px-3 py-0.5 font-mono text-[10px]" style={{ backgroundColor: "rgba(128,128,128,0.15)", color: t.muted }}>
                    www.{t.id}travel.com
                  </span>
                </div>
                <div className="grid sm:grid-cols-2">
                  <div className="flex flex-col justify-center p-7 sm:p-9">
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: t.accent }}>{t.name}</p>
                    <p className="mt-3 font-serif text-2xl leading-tight sm:text-3xl" style={{ color: t.fg }}>{t.tagline}</p>
                    <div className="mt-6 space-y-2">
                      {t.pkgs.map((p) => (
                        <div key={p} className="flex items-center justify-between rounded-xl border px-4 py-3" style={{ borderColor: "rgba(128,128,128,0.25)" }}>
                          <span className="text-sm font-semibold" style={{ color: t.fg }}>{p}</span>
                          <span className="rounded-full px-3 py-1 text-[10px] font-bold" style={{ backgroundColor: t.accent, color: t.id === "maldives" ? "#0B1F33" : "#fff" }}>
                            Plan This Trip
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: t.muted }}>Powered by Travel-Tek</p>
                  </div>
                  <div className="relative min-h-[220px]">
                    <img src={t.img} alt={`${t.name} website hero`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
